import React, { useState } from 'react';
import {
  AlertTriangle,
  EyeOff,
  Landmark,
  Lock,
  Pencil,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import {
  usePayrollBankConfigurations,
  useSavePayrollBankConfiguration,
} from '../../hooks';
import Modal from '../UI/Modal';
import { StatusBadge } from '../UI/StatusBadge';

const createEmptyForm = (defaultBic) => ({
  debitAccount: '',
  confirmDebitAccount: '',
  debitBic: defaultBic,
  processingMode: 'BA',
  serviceType: '',
  isActive: true,
});

const BankFileConfigurationCard = ({
  providerId,
  providerName,
  formatLabel,
  defaultBic,
  showAdvancedFields = false,
}) => {
  const {
    data: configurations = [],
    isLoading,
    isError,
    error,
    refetch,
  } = usePayrollBankConfigurations();
  const saveMutation = useSavePayrollBankConfiguration();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(() => createEmptyForm(defaultBic));
  const formId = `payroll-bank-configuration-form-${providerId}`;

  const configuration = configurations.find(
    (item) => item.provider_id === providerId,
  ) || null;
  const hasConfiguration = !!configuration;
  const isConfigured = !!configuration?.configured;

  const openConfiguration = () => {
    setForm({
      debitAccount: '',
      confirmDebitAccount: '',
      debitBic: configuration?.debit_bic || defaultBic,
      processingMode: configuration?.processing_mode || 'BA',
      serviceType: configuration?.service_type || '',
      isActive: configuration?.is_active ?? true,
    });
    setModalOpen(true);
  };

  const closeConfiguration = () => {
    if (saveMutation.isPending) return;
    setModalOpen(false);
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const debitAccount = form.debitAccount.replace(/\D/g, '');
    const confirmDebitAccount = form.confirmDebitAccount.replace(/\D/g, '');
    const payload = {
      provider_id: providerId,
      debit_bic: form.debitBic,
      is_active: form.isActive,
      ...(showAdvancedFields
        ? {
          processing_mode: form.processingMode,
          service_type: form.serviceType,
        }
        : {}),
      ...(debitAccount
        ? {
          debit_account: debitAccount,
          confirm_debit_account: confirmDebitAccount,
        }
        : {}),
    };

    saveMutation.mutate(
      { id: configuration?.id, data: payload },
      { onSuccess: () => setModalOpen(false) },
    );
  };

  const debitAccountDigits = form.debitAccount.replace(/\D/g, '');
  const confirmDebitAccountDigits = form.confirmDebitAccount.replace(/\D/g, '');
  const debitAccountChanged = !!form.debitAccount.trim();
  const debitAccountValid = (
    debitAccountDigits.length >= 6
    && debitAccountDigits.length <= 34
  );
  const debitAccountError = debitAccountChanged && !debitAccountValid;
  const confirmationError = (
    debitAccountChanged
    && debitAccountValid
    && debitAccountDigits !== confirmDebitAccountDigits
  );
  const accountsMatch = (
    !debitAccountChanged
    || (debitAccountValid && !confirmationError)
  );

  return (
    <>
      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex min-w-0 items-start">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
              <Landmark className="h-5 w-5 text-red-600" />
            </div>
            <div className="ml-3 min-w-0">
              <h3 className="font-semibold text-gray-900">
                {providerName}
              </h3>
              <p className="mt-0.5 text-xs text-gray-500">
                {formatLabel}
              </p>
            </div>
          </div>
          {!isLoading && !isError && (
            <button
              type="button"
              onClick={openConfiguration}
              className={isConfigured
                ? 'btn btn-admin-outline'
                : 'btn btn-admin-primary'}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {hasConfiguration
                ? 'Edit Configuration'
                : 'Configure Bank File'}
            </button>
          )}
        </div>

        <div className="p-4 sm:p-6">
          {isLoading && (
            <div
              role="status"
              className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500"
            >
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading secure configuration…
            </div>
          )}

          {isError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4"
            >
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-red-800">
                  Bank configuration could not be loaded
                </p>
                <p className="mt-1 text-xs text-red-700">
                  {error?.response?.data?.detail
                    || 'Confirm that you have HR Admin access and try again.'}
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="mt-3 text-xs font-medium text-red-700 underline hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {!isLoading && !isError && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Payroll debit configuration
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Applied to both Worker and Staff payment files
                  </p>
                </div>
                {isConfigured ? (
                  <StatusBadge
                    status="approved"
                    label="Configured"
                    showIcon
                  />
                ) : hasConfiguration ? (
                  <StatusBadge
                    status="inactive"
                    label="Inactive"
                    showIcon
                  />
                ) : (
                  <StatusBadge
                    status="pending"
                    label="Setup required"
                    showIcon
                  />
                )}
              </div>

              <div className={`grid grid-cols-2 gap-3 ${
                showAdvancedFields ? 'lg:grid-cols-4' : ''
              }`}
              >
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Debit account</p>
                  <p className="mt-1 font-medium text-gray-900">
                    {configuration?.account_masked || 'Not configured'}
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">BIC / SWIFT</p>
                  <p className="mt-1 font-medium text-gray-900">
                    {configuration?.debit_bic || defaultBic}
                  </p>
                </div>
                {showAdvancedFields && (
                  <>
                    <div className="rounded-xl bg-gray-50 p-3">
                      <p className="text-xs text-gray-500">Processing mode</p>
                      <p className="mt-1 font-medium text-gray-900">
                        {configuration?.processing_mode || 'BA'}
                      </p>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-3">
                      <p className="text-xs text-gray-500">Service type</p>
                      <p className="mt-1 font-medium text-gray-900">
                        {configuration?.service_type || 'Bank default'}
                      </p>
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                <p className="text-xs text-blue-800">
                  The debit account is encrypted at rest, never returned by the
                  API, and only its final four digits are displayed. Changes are
                  recorded in the audit log.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <Modal
        isOpen={modalOpen}
        onClose={closeConfiguration}
        title={`${providerName} Bank File`}
        size="md"
        theme="admin"
        closable={!saveMutation.isPending}
        footer={(
          <>
            <button
              type="button"
              onClick={closeConfiguration}
              disabled={saveMutation.isPending}
              className="btn btn-admin-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              form={formId}
              disabled={(
                saveMutation.isPending
                || (!configuration && !debitAccountValid)
                || !accountsMatch
              )}
              className="btn btn-admin-primary"
            >
              <Lock className="mr-2 h-4 w-4" />
              {saveMutation.isPending ? 'Saving…' : 'Save Securely'}
            </button>
          </>
        )}
      >
        <form
          id={formId}
          onSubmit={handleSubmit}
          className="space-y-5"
        >
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-start gap-3">
              <EyeOff className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Write-only debit account
                </p>
                <p className="mt-1 text-xs text-gray-600">
                  After saving, the full account number cannot be retrieved in
                  this portal. Enter a new value only when replacing it.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor={`${providerId}-debit-account`}
              className="form-label-admin"
            >
              Company debit account
              {!configuration && <span className="text-red-600"> *</span>}
            </label>
            <input
              id={`${providerId}-debit-account`}
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              value={form.debitAccount}
              onChange={(event) => setField('debitAccount', event.target.value)}
              placeholder={configuration
                ? `Leave blank to keep ${configuration.account_masked}`
                : 'Enter the registered company debit account'}
              required={!configuration}
              aria-invalid={debitAccountError}
              aria-describedby={debitAccountError
                ? `${providerId}-debit-account-error`
                : undefined}
              className={`input-admin ${debitAccountError ? 'input-error' : ''}`}
            />
            {debitAccountError && (
              <p
                id={`${providerId}-debit-account-error`}
                className="mt-1 text-xs text-red-600"
              >
                Enter a valid debit account number (6–34 digits).
              </p>
            )}
          </div>

          {debitAccountChanged && (
            <div>
              <label
                htmlFor={`${providerId}-debit-account-confirmation`}
                className="form-label-admin"
              >
                Confirm debit account <span className="text-red-600">*</span>
              </label>
              <input
                id={`${providerId}-debit-account-confirmation`}
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                value={form.confirmDebitAccount}
                onChange={(event) => setField(
                  'confirmDebitAccount',
                  event.target.value,
                )}
                required
                aria-invalid={confirmationError}
                aria-describedby={confirmationError
                  ? `${providerId}-debit-account-confirmation-error`
                  : undefined}
                className={`input-admin ${confirmationError ? 'input-error' : ''}`}
              />
              {confirmationError && (
                <p
                  id={`${providerId}-debit-account-confirmation-error`}
                  className="mt-1 text-xs text-red-600"
                >
                  Debit account numbers do not match.
                </p>
              )}
            </div>
          )}

          {showAdvancedFields && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor={`${providerId}-debit-bic`}
                    className="form-label-admin"
                  >
                    BIC / SWIFT
                  </label>
                  <input
                    id={`${providerId}-debit-bic`}
                    type="text"
                    value={form.debitBic}
                    onChange={(event) => setField('debitBic', event.target.value)}
                    maxLength={11}
                    className="input-admin uppercase"
                  />
                </div>
                <div>
                  <label
                    htmlFor={`${providerId}-processing-mode`}
                    className="form-label-admin"
                  >
                    Processing mode
                  </label>
                  <input
                    id={`${providerId}-processing-mode`}
                    type="text"
                    value={form.processingMode}
                    onChange={(event) => setField(
                      'processingMode',
                      event.target.value,
                    )}
                    maxLength={2}
                    className="input-admin uppercase"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor={`${providerId}-service-type`}
                  className="form-label-admin"
                >
                  Service type
                  <span className="ml-1 font-normal text-gray-500">
                    (optional)
                  </span>
                </label>
                <input
                  id={`${providerId}-service-type`}
                  type="text"
                  value={form.serviceType}
                  onChange={(event) => setField('serviceType', event.target.value)}
                  maxLength={2}
                  placeholder="Use the bank-configured default"
                  className="input-admin uppercase"
                />
              </div>
            </>
          )}

          <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-4 rounded-xl border border-gray-200 p-3">
            <span>
              <span className="block text-sm font-medium text-gray-900">
                Active for payroll exports
              </span>
              <span className="mt-0.5 block text-xs text-gray-500">
                Inactive configurations block new file generation.
              </span>
            </span>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setField('isActive', event.target.checked)}
              className="h-5 w-5 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500"
            />
          </label>
        </form>
      </Modal>
    </>
  );
};

export default BankFileConfigurationCard;
